/* global PowerPoint, Office */
import JSZip from 'jszip';
import { sandboxedEval } from './sandbox';
import { assertFormulasAllowed } from './formulaSecurity';
import {
  analyzePowerPointTemplate,
  createPowerPointTemplateCarrier,
  isBlankPowerPointSlideExport,
} from './powerPointTemplate';

/**
 * PowerPointBridge - Office.js code execution engine for PowerPoint
 *
 * Core mechanism: The backend Claude agent generates Office.js JavaScript code.
 * This bridge evaluates that code inside PowerPoint.run() context.
 *
 * Tools implemented (matching Anthropic's Claude for PowerPoint):
 *  - executeCode(code)                  → eval arbitrary Office.js inside PowerPoint.run()
 *  - screenshotSlide(slideIndex)        → capture slide as PNG image blocks
 *  - editSlideXml(slideIndex, code)     → raw OOXML manipulation via JSZip
 *  - editSlideChart(slideIndex, code)   → chart creation via OOXML
 *  - editSlideMaster(code)             → theme/master editing via OOXML
 *  - readSlideText(slideIndex, shapeId) → read OOXML paragraph XML
 *  - editSlideText(slideIndex, shapeId, code) → replace paragraph XML
 *  - duplicateSlide(slideIndex)         → clone a slide
 *  - verifySlides()                     → layout validation (overlap, bounds, placeholders)
 *  - getInitialState()                  → deck metadata for context injection
 *  - getUserContext()                    → current selection state
 */

// =============================================================================
// OOXML NAMESPACE CONSTANTS
// =============================================================================

const NS_A = 'http://schemas.openxmlformats.org/drawingml/2006/main';
const NS_P = 'http://schemas.openxmlformats.org/presentationml/2006/main';

// =============================================================================
// UTILITY: XML Escaping
// =============================================================================

/**
 * Escape special characters for safe embedding in XML text content.
 * @param {string} text - Raw text to escape
 * @returns {string} XML-safe text
 */
export function escapeXml(text) {
  if (typeof text !== 'string') return String(text ?? '');
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

const EMBEDDED_WORKBOOK_LIMITS = Object.freeze({
  maxArchiveBytes: 20 * 1024 * 1024,
  maxCompressedBytes: 20 * 1024 * 1024,
  maxUncompressedBytes: 100 * 1024 * 1024,
  maxEntryBytes: 20 * 1024 * 1024,
  maxEntries: 512,
  maxWorkbooksPerSlidePackage: 32,
});

const FORBIDDEN_EMBEDDED_WORKBOOK_PART_RE =
  /(^|\/)(?:activex|customxml|embeddings|externallinks|querytables|webextensions)(?:\/|$)|(?:^|\/)connections\.xml$|(?:^|\/)vbaproject\.bin$/i;

const FORBIDDEN_EMBEDDED_WORKBOOK_CONTENT_TYPE_RE =
  /macroenabled|vba|activex|externalLink|connections|customXml|webextension|oleObject/i;

function assertEmbeddedWorkbookPath(path) {
  const raw = String(path || '');
  const normalized = raw.replace(/\\/g, '/');
  if (
    raw !== normalized ||
    !/^ppt\/embeddings\/[^/]+\.xlsx$/i.test(normalized) ||
    normalized.includes('..')
  ) {
    throw new Error(`Unsupported embedded workbook path: ${path}`);
  }
  return normalized;
}

function getKnownZipEntrySize(file, property) {
  const value = file?._data?.[property];
  return Number.isFinite(value) && value >= 0 ? value : null;
}

function mergeEmbeddedWorkbookLimits(overrides = {}) {
  const limits = { ...EMBEDDED_WORKBOOK_LIMITS, ...overrides };
  for (const [name, value] of Object.entries(limits)) {
    if (!Number.isSafeInteger(value) || value <= 0) {
      throw new Error(`Invalid embedded workbook limit: ${name}`);
    }
  }
  return limits;
}

function assertWorkbookRelationshipTargetsAreInternal(xml, label) {
  const doc = parseXmlSafe(xml, label);
  const relationships = doc.getElementsByTagNameNS('*', 'Relationship');
  for (let index = 0; index < relationships.length; index++) {
    const relationship = relationships[index];
    if (String(relationship.getAttribute('TargetMode') || '').trim().toLowerCase() === 'external') {
      throw new Error(`Embedded workbook external relationship is not allowed: ${label}`);
    }
  }
}

const EMBEDDED_WORKBOOK_FORMULA_NODE_NAMES = new Set([
  'calculatedcolumnformula',
  'definedname',
  'f',
  'formula',
  'formula1',
  'formula2',
  'totalsrowformula',
]);

const EMBEDDED_WORKBOOK_FORMULA_ATTRIBUTE_NAMES = new Set([
  'formula',
  'formula1',
  'formula2',
  'refersto',
]);

function normalizedXmlLocalName(node) {
  return String(node?.localName || node?.nodeName || '')
    .split(':')
    .pop()
    .toLowerCase();
}

function appendFormulaCandidate(formulas, value) {
  const text = String(value || '').trim();
  if (text) formulas.push(text.startsWith('=') ? text : `=${text}`);
}

function assertWorkbookXmlFormulasAreSafe(xml, label) {
  const doc = parseXmlSafe(xml, label);
  const formulas = [];
  const nodes = doc.getElementsByTagName('*');
  for (let index = 0; index < nodes.length; index++) {
    const node = nodes[index];
    if (EMBEDDED_WORKBOOK_FORMULA_NODE_NAMES.has(normalizedXmlLocalName(node))) {
      appendFormulaCandidate(formulas, node.textContent);
    }
    for (const attribute of Array.from(node.attributes || [])) {
      if (EMBEDDED_WORKBOOK_FORMULA_ATTRIBUTE_NAMES.has(normalizedXmlLocalName(attribute))) {
        appendFormulaCandidate(formulas, attribute.value);
      }
    }
  }
  assertFormulasAllowed(formulas);
}

async function snapshotPresentationEmbeddings(rootZip) {
  const entries = new Map();
  for (const [entryPath, file] of Object.entries(rootZip?.files || {})) {
    if (/^ppt\/embeddings\/.+/i.test(entryPath) && !file.dir) {
      entries.set(entryPath, await file.async('base64'));
    }
  }
  return entries;
}

function changedEmbeddingEntryPaths(before, after) {
  const paths = new Set([...before.keys(), ...after.keys()]);
  return [...paths].filter((entryPath) => before.get(entryPath) !== after.get(entryPath));
}

/**
 * Validate an embedded XLSX package before it is exposed to generated code or
 * reinserted into PowerPoint. The optional limits argument exists so tests can
 * exercise the same production checks with small fixtures.
 */
export async function validateEmbeddedWorkbookPackage(
  workbookZip,
  { archiveByteLength = null, limits: limitOverrides = {} } = {}
) {
  if (!workbookZip || typeof workbookZip.file !== 'function') {
    throw new Error('A valid embedded workbook package is required');
  }

  const limits = mergeEmbeddedWorkbookLimits(limitOverrides);
  if (archiveByteLength !== null && (
    !Number.isSafeInteger(archiveByteLength) ||
    archiveByteLength < 0 ||
    archiveByteLength > limits.maxArchiveBytes
  )) {
    throw new Error('Embedded workbook archive exceeds the compressed size limit');
  }

  const entries = Object.entries(workbookZip.files).filter(([, file]) => !file.dir);
  if (entries.length === 0 || entries.length > limits.maxEntries) {
    throw new Error('Embedded workbook entry count exceeds the allowed limit');
  }

  let compressedBytes = 0;
  let uncompressedBytes = 0;
  for (const [entryPath, file] of entries) {
    const normalizedPath = String(entryPath || '').replace(/\\/g, '/');
    if (
      normalizedPath.startsWith('/') ||
      normalizedPath.split('/').includes('..') ||
      FORBIDDEN_EMBEDDED_WORKBOOK_PART_RE.test(normalizedPath)
    ) {
      throw new Error(`Embedded workbook contains a forbidden part: ${entryPath}`);
    }

    const knownCompressed = getKnownZipEntrySize(file, 'compressedSize');
    const knownUncompressed = getKnownZipEntrySize(file, 'uncompressedSize');
    if (knownCompressed !== null) compressedBytes += knownCompressed;
    if (knownUncompressed !== null) {
      if (knownUncompressed > limits.maxEntryBytes) {
        throw new Error(`Embedded workbook part exceeds the size limit: ${entryPath}`);
      }
      uncompressedBytes += knownUncompressed;
    }
    if (compressedBytes > limits.maxCompressedBytes) {
      throw new Error('Embedded workbook compressed content exceeds the size limit');
    }
    if (uncompressedBytes > limits.maxUncompressedBytes) {
      throw new Error('Embedded workbook uncompressed content exceeds the size limit');
    }
  }

  for (const [entryPath, file] of entries) {
    const knownUncompressed = getKnownZipEntrySize(file, 'uncompressedSize');
    let bytes = null;
    if (knownUncompressed === null) {
      bytes = await file.async('uint8array');
      if (bytes.byteLength > limits.maxEntryBytes) {
        throw new Error(`Embedded workbook part exceeds the size limit: ${entryPath}`);
      }
      uncompressedBytes += bytes.byteLength;
      if (uncompressedBytes > limits.maxUncompressedBytes) {
        throw new Error('Embedded workbook uncompressed content exceeds the size limit');
      }
    }

    const lowerPath = entryPath.toLowerCase();
    if (lowerPath === '[content_types].xml') {
      const xml = await file.async('string');
      if (FORBIDDEN_EMBEDDED_WORKBOOK_CONTENT_TYPE_RE.test(xml)) {
        throw new Error('Embedded workbook declares a forbidden content type');
      }
    } else if (lowerPath.endsWith('.rels')) {
      const xml = await file.async('string');
      assertWorkbookRelationshipTargetsAreInternal(xml, entryPath);
    } else if (lowerPath.startsWith('xl/') && lowerPath.endsWith('.xml')) {
      const xml = await file.async('string');
      assertWorkbookXmlFormulasAreSafe(xml, entryPath);
    }
  }

  if (!workbookZip.file('[Content_Types].xml') || !workbookZip.file('xl/workbook.xml')) {
    throw new Error('Embedded workbook is missing required XLSX parts');
  }
  return workbookZip;
}

export async function validateEmbeddedWorkbooksInPresentationPackage(
  rootZip,
  { embeddingPaths = null, limits: limitOverrides = {} } = {}
) {
  if (!rootZip || typeof rootZip.file !== 'function') {
    throw new Error('A valid PowerPoint package is required');
  }
  const limits = mergeEmbeddedWorkbookLimits(limitOverrides);
  const candidatePaths = embeddingPaths === null
    ? Object.keys(rootZip.files)
    : [...new Set(embeddingPaths)];
  const workbookPaths = candidatePaths
    .filter((entryPath) => {
      const file = rootZip.files[entryPath];
      return /^ppt\/embeddings\/.+/i.test(entryPath) && file && !file.dir;
    })
    .map(assertEmbeddedWorkbookPath);
  if (workbookPaths.length > limits.maxWorkbooksPerSlidePackage) {
    throw new Error('PowerPoint package contains too many embedded workbooks');
  }
  for (const workbookPath of workbookPaths) {
    const workbookFile = rootZip.file(workbookPath);
    const knownArchiveBytes = getKnownZipEntrySize(workbookFile, 'uncompressedSize');
    if (knownArchiveBytes !== null && knownArchiveBytes > limits.maxArchiveBytes) {
      throw new Error(`Embedded workbook archive exceeds the size limit: ${workbookPath}`);
    }
    const bytes = await workbookFile.async('uint8array');
    if (bytes.byteLength > limits.maxArchiveBytes) {
      throw new Error(`Embedded workbook archive exceeds the size limit: ${workbookPath}`);
    }
    const workbookZip = await JSZip.loadAsync(bytes);
    await validateEmbeddedWorkbookPackage(workbookZip, {
      archiveByteLength: bytes.byteLength,
      limits,
    });
  }
}

/**
 * Open an embedded OOXML workbook from a slide package without mutating the
 * parent package. Exposed to the chart-edit sandbox for durable chart updates.
 */
export async function loadEmbeddedWorkbook(rootZip, path) {
  const normalized = assertEmbeddedWorkbookPath(path);
  const file = rootZip?.file(normalized);
  if (!file) throw new Error(`Embedded workbook not found: ${normalized}`);
  const knownArchiveBytes = getKnownZipEntrySize(file, 'uncompressedSize');
  if (knownArchiveBytes !== null && knownArchiveBytes > EMBEDDED_WORKBOOK_LIMITS.maxArchiveBytes) {
    throw new Error('Embedded workbook archive exceeds the size limit');
  }
  const bytes = await file.async('uint8array');
  if (bytes.byteLength > EMBEDDED_WORKBOOK_LIMITS.maxArchiveBytes) {
    throw new Error('Embedded workbook archive exceeds the size limit');
  }
  const workbookZip = await JSZip.loadAsync(bytes);
  return validateEmbeddedWorkbookPackage(workbookZip, { archiveByteLength: bytes.byteLength });
}

/**
 * Replace an embedded workbook after its source cells have been updated.
 * The caller still controls the parent slide's markDirty boundary.
 */
export async function saveEmbeddedWorkbook(rootZip, path, workbookZip) {
  const normalized = assertEmbeddedWorkbookPath(path);
  if (!workbookZip || typeof workbookZip.generateAsync !== 'function') {
    throw new Error('A valid embedded workbook package is required');
  }
  await validateEmbeddedWorkbookPackage(workbookZip);
  const bytes = await workbookZip.generateAsync({ type: 'uint8array' });
  if (bytes.byteLength > EMBEDDED_WORKBOOK_LIMITS.maxArchiveBytes) {
    throw new Error('Embedded workbook archive exceeds the size limit');
  }
  const generatedWorkbook = await JSZip.loadAsync(bytes);
  await validateEmbeddedWorkbookPackage(generatedWorkbook, { archiveByteLength: bytes.byteLength });
  rootZip.file(normalized, bytes);
  return normalized;
}

/**
 * Fix bare ampersands in XML that would break the parser.
 * Matches & not followed by a valid XML entity reference.
 * @param {string} xml - Potentially malformed XML string
 * @returns {string} Fixed XML
 */
// =============================================================================
// ICON HELPERS (insert_icon)
// =============================================================================

const EMU_PER_PT = 12700;
const NS_REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const ICON_PNG_SIZE = 192;
const TEMPLATE_TAG_KEYS = {
  fingerprint: 'RESTACK_TEMPLATE_FINGERPRINT',
  masterId: 'RESTACK_TEMPLATE_MASTER_ID',
  layoutId: 'RESTACK_TEMPLATE_LAYOUT_ID',
  workingSlideId: 'RESTACK_TEMPLATE_WORKING_SLIDE_ID',
  applyScope: 'RESTACK_TEMPLATE_APPLY_SCOPE',
};

const DALOOPA_CITATION_SHAPE_NAME = 'Restack Daloopa Sources';
const DALOOPA_CITATION_ID_RE = /^cx_[A-Za-z0-9_-]{8,128}$/;
const DALOOPA_CITATION_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z)?$/;
const DALOOPA_DATASET_BY_QUERY_CLASS = Object.freeze({
  company_lookup: 'Company Directory',
  series_discovery: 'Financial Series Catalog',
  document_discovery: 'Company Documents',
  fundamentals: 'Company Fundamentals',
  document_content: 'Company Documents',
  prices: 'Market Prices',
  document_search: 'Company Documents',
});

function validateDaloopaCitations(citations) {
  if (!Array.isArray(citations) || citations.length < 1 || citations.length > 100) {
    throw new Error('One to 100 Daloopa citations are required');
  }
  const seenIds = new Set();
  return citations.map((citation) => {
    const isRecord = citation !== null && typeof citation === 'object' && !Array.isArray(citation);
    const asOfDateIsValid = citation?.asOfDate === null || (
      typeof citation?.asOfDate === 'string' &&
      citation.asOfDate.length <= 40 &&
      DALOOPA_CITATION_DATE_RE.test(citation.asOfDate) &&
      Number.isFinite(Date.parse(citation.asOfDate))
    );
    const retrievedAtIsValid = typeof citation?.retrievedAt === 'string' &&
      citation.retrievedAt.length <= 40 &&
      DALOOPA_CITATION_DATE_RE.test(citation.retrievedAt) &&
      Number.isFinite(Date.parse(citation.retrievedAt));
    const expectedDataset = DALOOPA_DATASET_BY_QUERY_CLASS[citation?.queryClass];
    const displayDate = (citation?.asOfDate || citation?.retrievedAt || '').slice(0, 10);
    const expectedMarker = isRecord
      ? `Source: Daloopa | ${citation.dataset} | ${citation.queryClass} | ${displayDate} | ${citation.citationId}`
      : '';
    if (
      !isRecord ||
      citation.providerName !== 'Daloopa' ||
      !DALOOPA_CITATION_ID_RE.test(citation.citationId || '') ||
      seenIds.has(citation.citationId) ||
      !expectedDataset ||
      citation.dataset !== expectedDataset ||
      !asOfDateIsValid ||
      !retrievedAtIsValid ||
      citation.marker !== expectedMarker ||
      citation.marker.length > 360
    ) {
      throw new Error('Daloopa citation metadata is invalid');
    }
    seenIds.add(citation.citationId);
    return Object.freeze({
      citationId: citation.citationId,
      marker: expectedMarker,
    });
  });
}

function validateDaloopaPowerPointTarget(target) {
  const keys = target && typeof target === 'object' && !Array.isArray(target)
    ? Object.keys(target).sort().join(',')
    : '';
  if (
    keys !== 'app,slide_index' ||
    target.app !== 'powerpoint' ||
    !Number.isInteger(target.slide_index) ||
    target.slide_index < 1 ||
    target.slide_index > 1000
  ) {
    throw new Error('A valid PowerPoint citation target is required');
  }
  return target.slide_index - 1;
}

/**
 * Recolor an SVG by injecting a fill style and tagging paintable elements.
 * Elements that already carry a class are left alone.
 */
function recolorSvg(svg, color) {
  const style = `<style>.iconFill{fill:${color};}</style>`;
  let result;
  if (/<style[\s\S]*?<\/style>/.test(svg)) {
    result = svg.replace(/<style[\s\S]*?<\/style>/, style);
  } else {
    const idx = svg.indexOf('>', svg.indexOf('<svg'));
    if (idx === -1) return svg;
    result = svg.slice(0, idx + 1) + style + svg.slice(idx + 1);
  }
  return result.replace(
    /<(path|rect|circle|polygon|ellipse)(\s[^>]*)?(\/?)>/g,
    (m, tag, attrs, slash) =>
      attrs && /\bclass=/.test(attrs)
        ? m
        : `<${tag} class="iconFill"${attrs || ''}${slash}>`
  );
}

/**
 * Rasterize an SVG string to a base64 PNG via canvas. PowerPoint requires a
 * PNG fallback alongside the vector (<asvg:svgBlip>) for compatibility.
 */
async function rasterizeSvgToPng(svg, width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to create canvas 2D context for SVG rasterization');

  const img = new Image();
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  try {
    await new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load SVG for rasterization'));
      img.src = url;
    });
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL('image/png').split(',')[1];
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Ensure a Default content-type registration exists for an extension in
 * [Content_Types].xml (e.g. svg -> image/svg+xml).
 */
async function registerContentType(zip, extension, contentType) {
  const path = '[Content_Types].xml';
  const xml = await zip.file(path).async('string');
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const defaults = doc.getElementsByTagName('Default');
  for (let i = 0; i < defaults.length; i++) {
    if ((defaults[i].getAttribute('Extension') || '').toLowerCase() === extension.toLowerCase()) {
      return; // already registered
    }
  }
  const ns = doc.documentElement.namespaceURI;
  const el = doc.createElementNS(ns, 'Default');
  el.setAttribute('Extension', extension);
  el.setAttribute('ContentType', contentType);
  doc.documentElement.appendChild(el);
  zip.file(path, new XMLSerializer().serializeToString(doc));
}

function sanitizeXmlAmpersands(xml) {
  if (typeof xml !== 'string') return xml;
  // Match & not followed by amp; lt; gt; quot; apos; #digits; or #xHex;
  return xml.replace(/&(?!(?:amp|lt|gt|quot|apos|#\d+|#x[\da-fA-F]+);)/g, '&amp;');
}

/**
 * Parse XML safely with parsererror detection.
 * DOMParser.parseFromString() silently returns a <parsererror> document
 * on malformed XML instead of throwing. This wrapper catches that.
 * @param {string} xmlString - XML to parse
 * @param {string} [label] - Label for error messages (e.g., "slide1.xml")
 * @returns {Document} - Parsed XML document
 * @throws {Error} if XML is malformed
 */
function parseXmlSafe(xmlString, label = 'XML') {
  const doc = new DOMParser().parseFromString(xmlString, 'application/xml');
  const errors = doc.getElementsByTagName('parsererror');
  if (errors.length > 0) {
    const errorText = errors[0].textContent?.slice(0, 200) || 'unknown parse error';
    throw new Error(`Malformed ${label}: ${errorText}`);
  }
  return doc;
}

/**
 * Extract all external references from .rels files in a ZIP archive.
 * Returns a Set of "file::Target" strings for before/after comparison.
 * @param {JSZip} zip - The JSZip archive
 * @returns {Promise<Set<string>>} Set of external reference identifiers
 */
export async function extractExternalReferences(zip) {
  const refs = new Set();
  const relsFiles = Object.keys(zip.files).filter(f => f.endsWith('.rels'));
  for (const relsFile of relsFiles) {
    const content = await zip.file(relsFile).async('string');
    const doc = parseXmlSafe(content, relsFile);
    const relationships = doc.getElementsByTagNameNS('*', 'Relationship');
    for (let index = 0; index < relationships.length; index++) {
      const relationship = relationships[index];
      if (String(relationship.getAttribute('TargetMode') || '').toLowerCase() !== 'external') {
        continue;
      }
      const target = String(relationship.getAttribute('Target') || '').trim();
      if (target) refs.add(`${relsFile}::${target}`);
    }
  }
  return refs;
}

export async function snapshotZipEntries(zip) {
  const entries = new Map();
  for (const [entryPath, file] of Object.entries(zip.files)) {
    if (!file.dir) entries.set(entryPath, await file.async('base64'));
  }
  return entries;
}

export function changedZipEntryPaths(before, after) {
  const paths = new Set([...before.keys(), ...after.keys()]);
  return [...paths].filter((entryPath) => before.get(entryPath) !== after.get(entryPath));
}

function assertChangedZipEntriesSurvived(expected, readback, changedPaths) {
  for (const entryPath of changedPaths) {
    if (expected.has(entryPath)) {
      if (readback.get(entryPath) !== expected.get(entryPath)) {
        throw new Error(`Private PowerPoint write verification failed for ${entryPath}`);
      }
    } else if (readback.has(entryPath)) {
      throw new Error(`Private PowerPoint deletion verification failed for ${entryPath}`);
    }
  }
}

/**
 * Decide whether a newly-injected external reference is safe to allow.
 *
 * Plain http(s) URLs are permitted — these are research citations, public
 * documentation links, etc. Anything with a more dangerous scheme
 * (file://, data:, javascript:, vbscript:, ms-its:, mhtml:, ldap:, smb://)
 * stays blocked because PowerPoint can resolve some of those at click-time
 * in ways the user wouldn't expect.
 *
 * @param {string} ref - Format: "{relsFile}::{url}"
 * @returns {boolean} true if the reference is safe to permit
 */
function isSafeExternalReference(ref) {
  const sepIdx = ref.indexOf('::');
  const url = (sepIdx >= 0 ? ref.slice(sepIdx + 2) : ref).trim();
  if (!url) return false;
  // Allow http and https. Everything else is denied by default.
  return /^https?:\/\//i.test(url);
}

export function assertNoNewExternalReferences(
  refsBefore,
  refsAfter,
  { allowHttpReferences = true } = {}
) {
  for (const ref of refsAfter) {
    if (refsBefore.has(ref)) continue;
    if (allowHttpReferences && isSafeExternalReference(ref)) continue;
    throw new Error(`Security: blocked new external reference: ${ref}`);
  }
}

/**
 * Find a shape element by name in a parsed slide XML document.
 * Exposed as a sandbox global for AI-generated edit_slide_xml code.
 * @param {Document} doc - Parsed XML document
 * @param {string} shapeName - Shape name to find
 * @param {number} [occurrence=0] - 0-based index for same-name shapes
 * @returns {Element|null} The matching <p:sp> element, or null
 */
function findShapeByName(doc, shapeName, occurrence = 0) {
  const spNodes = doc.getElementsByTagNameNS(NS_P, 'sp');
  let matchCount = 0;
  for (let i = 0; i < spNodes.length; i++) {
    const sp = spNodes[i];
    const nvSpPr = sp.getElementsByTagNameNS(NS_P, 'nvSpPr')[0];
    if (!nvSpPr) continue;
    const cNvPr = nvSpPr.getElementsByTagNameNS(NS_P, 'cNvPr')[0];
    if (!cNvPr) continue;
    if (cNvPr.getAttribute('name') !== shapeName) continue;
    if (matchCount < occurrence) { matchCount++; continue; }
    return sp;
  }
  return null;
}

// =============================================================================
// TIMEOUT GUARD
// =============================================================================

const TOOL_TIMEOUT_MS = 80_000; // 80s — under backend's 90s timeout

/**
 * Race a promise against a timeout. If the promise doesn't resolve
 * within the given ms, reject with a descriptive error.
 * @param {Promise} promise - The promise to race
 * @param {number} ms - Timeout in milliseconds
 * @param {string} label - Label for the error message
 * @returns {Promise} - Resolves with the original promise or rejects on timeout
 */
function withTimeout(promise, ms, label = 'Office.js call') {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms / 1000}s — try a simpler operation or break it into smaller steps`));
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function isSesInitializationError(error) {
  const message = String(error?.message || error || '');
  return message.includes('Compartment') ||
    message.includes('lockdown') ||
    message.includes('SES');
}

function createSesUnavailableError(toolName) {
  return new Error(
    `SES sandbox unavailable — ${toolName} is disabled until the sandbox initializes successfully`
  );
}

// =============================================================================
// MAIN CLASS
// =============================================================================

export class PowerPointBridge {
  constructor({ isOfficeOnline = false } = {}) {
    this._ready = false;
    this._lastError = null;
    this._isOfficeOnline = isOfficeOnline;
    // Office Online serialization queue (1.1) — prevents concurrent PowerPoint.run() crashes
    this._onlineRunQueue = Promise.resolve();
  }

  /**
   * Safe wrapper around PowerPoint.run() that serializes calls on Office Online.
   * Desktop can handle concurrent calls; Office Online crashes with GeneralException.
   * @param {Function} fn - Callback receiving (context)
   * @returns {Promise<any>}
   */
  _safeOfficeRun(fn, { timeoutMs = TOOL_TIMEOUT_MS, label = 'Office.run', signal = null } = {}) {
    // Bound EVERY run with a timeout measured from when the run ACTUALLY starts
    // (after the queue clears, on Office web). This is the key to preventing
    // cascades: a slow or hung PowerPoint.run can't consume a later call's time
    // budget, and the serialized queue always advances within `timeoutMs`.
    // (async () => ...) ensures even a synchronous throw becomes a rejection.
    const exec = () => {
      if (signal?.aborted) {
        const error = new Error(`${label} was cancelled before Office execution started`);
        error.name = 'AbortError';
        return Promise.reject(error);
      }
      return withTimeout((async () => PowerPoint.run(fn))(), timeoutMs, label);
    };

    // Desktop: runs are reliable and may overlap safely — no serialization.
    if (!this._isOfficeOnline) {
      return exec();
    }

    // Office web: concurrent PowerPoint.run calls crash with GeneralException,
    // so serialize them. Chain each run after the previous one SETTLES. The
    // chain NEVER rejects (so one failure can't break the queue for everyone)
    // and NEVER stays pending (exec is always timeout-bounded), so the queue
    // self-heals within `timeoutMs` even if a run hangs.
    const run = this._onlineRunQueue.then(exec, exec);
    this._onlineRunQueue = run.then(() => {}, () => {});
    return run;
  }

  // ===========================================================================
  // CORE: Execute arbitrary Office.js code
  // ===========================================================================

  /**
   * Execute arbitrary Office.js code inside PowerPoint.run().
   * Uses SES Compartment sandbox for security.
   *
   * @param {string} code - JavaScript code string to execute inside PowerPoint.run()
   * @returns {Promise<any>} - Result of the code execution
   */
  async executeCode(code) {
    if (typeof PowerPoint === 'undefined') {
      throw new Error('PowerPoint API not available');
    }

    return this._safeOfficeRun(async (context) => {
      let result;
      try {
        // Primary path: SES Compartment sandbox
        result = await sandboxedEval(code, {
          context,
          PowerPoint,
        });
      } catch (sesError) {
        if (isSesInitializationError(sesError)) {
          throw createSesUnavailableError('execute_office_js');
        }
        throw sesError;
      }
      await context.sync();
      return result;
    }, { timeoutMs: TOOL_TIMEOUT_MS, label: 'execute_office_js' });
  }

  // ===========================================================================
  // TOOL: screenshot_slide
  // ===========================================================================

  /**
   * Capture a slide as a base64 PNG image for visual verification.
   * @param {number} slideIndex - 0-based slide index
   * @returns {Promise<Object>} - ToolResult-style image block payload
   */
  async screenshotSlide(slideIndex, options = {}) {
    if (typeof PowerPoint === 'undefined') {
      throw new Error('PowerPoint API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      if (slideIndex < 0 || slideIndex >= slides.items.length) {
        throw new Error(`Slide index ${slideIndex} out of range (0-${slides.items.length - 1})`);
      }

      const slide = slides.items[slideIndex];

      try {
        const imageResult = slide.getImageAsBase64({ width: 960 });
        await context.sync();
        return {
          _images: [{ data: imageResult.value, mediaType: 'image/png' }],
        };
      } catch (imgError) {
        throw new Error(
          'Screenshot not available on this platform. Use read_deck_content or read_slide_text instead to inspect slide content.'
        );
      }
    }, options);
  }

  // ===========================================================================
  // OOXML ENGINE: withSlideZip
  // ===========================================================================

  /**
   * Core OOXML manipulation engine. Exports a single slide as base64,
   * creates a JSZip archive, runs callback code, sanitizes XML,
   * and reimports the modified slide.
   *
   * @param {number} slideIndex - 0-based slide index
   * @param {string} code - Async function body receiving { zip, markDirty }
   * @returns {Promise<string>} - Result description
   */
  async withSlideZip(slideIndex, code, options = {}) {
    // AI-generated code string: run it through the SES sandbox inside the
    // shared export/modify/reimport engine below.
    return this._withSlideZipNative(slideIndex, async ({ zip, markDirty }) => {
      try {
        return await sandboxedEval(code, {
          zip,
          markDirty,
          escapeXml,
          DOMParser,
          XMLSerializer,
          findShapeByName,
          loadEmbeddedWorkbook,
          saveEmbeddedWorkbook,
        });
      } catch (sesError) {
        if (isSesInitializationError(sesError)) {
          throw createSesUnavailableError('OOXML editing');
        }
        throw sesError;
      }
    }, { ...options, validateEmbeddedWorkbooks: true });
  }

  /**
   * Same export/modify/reimport engine, but with a TRUSTED native callback
   * (our own code, e.g. insertIcon) instead of a sandboxed AI code string.
   * All safety steps (sanitization, external-reference diff) still apply.
   *
   * @param {number} slideIndex - 0-based slide index
   * @param {Function} callbackFn - async ({ zip, markDirty }) => result
   */
  async _withSlideZipNative(
    slideIndex,
    callbackFn,
    {
      validateEmbeddedWorkbooks = false,
      allowHttpReferences = true,
      requireWriteVerification = false,
      signal = null,
    } = {}
  ) {
    if (typeof PowerPoint === 'undefined') {
      throw new Error('PowerPoint API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      if (slideIndex < 0 || slideIndex >= slides.items.length) {
        throw new Error(`Slide index ${slideIndex} out of range (0-${slides.items.length - 1})`);
      }

      // 1.3: Save current slide selection to restore after reimport
      let selectedIndices = [];
      try {
        const selected = context.presentation.getSelectedSlides();
        selected.load('items/id');
        await context.sync();
        const idToIndex = new Map(slides.items.map((s, idx) => [s.id, idx]));
        selectedIndices = selected.items
          .map((selectedSlide) => idToIndex.get(selectedSlide.id))
          .filter((idx) => Number.isInteger(idx));
      } catch {
        // getSelectedSlides not available on all platforms
      }

      const slide = slides.items[slideIndex];

      // Step 1: Export slide as base64
      const base64Result = slide.exportAsBase64();
      await context.sync();
      const base64Data = base64Result.value;

      // Step 2: Create JSZip archive
      const zip = await JSZip.loadAsync(base64Data, { base64: true });

      const embeddingsBefore = validateEmbeddedWorkbooks
        ? await snapshotPresentationEmbeddings(zip)
        : null;

      // Step 2.5: Snapshot external references BEFORE callback (for before/after comparison)
      const refsBefore = await extractExternalReferences(zip);
      const packageBefore = requireWriteVerification
        ? await snapshotZipEntries(zip)
        : null;

      // Step 3: Run callback with { zip, markDirty }
      let isDirty = false;
      const markDirty = () => { isDirty = true; };

      const callbackResult = await callbackFn({ zip, markDirty });

      if (!isDirty) {
        if (requireWriteVerification) {
          throw new Error('Private PowerPoint write did not mark a native mutation');
        }
        return callbackResult ?? 'No modifications made (markDirty() not called)';
      }

      if (validateEmbeddedWorkbooks) {
        const embeddingsAfter = await snapshotPresentationEmbeddings(zip);
        const changedEmbeddingPaths = changedEmbeddingEntryPaths(embeddingsBefore, embeddingsAfter);
        await validateEmbeddedWorkbooksInPresentationPackage(zip, {
          embeddingPaths: changedEmbeddingPaths,
        });
      }

      // Step 4: Sanitize XML in all .xml files
      for (const [path, file] of Object.entries(zip.files)) {
        if (path.endsWith('.xml') && !file.dir) {
          const content = await file.async('string');
          const sanitized = sanitizeXmlAmpersands(content);
          if (sanitized !== content) {
            zip.file(path, sanitized);
          }
        }
      }

      // Step 5: Check for NEW external references (before/after comparison).
      // http(s) URLs are permitted (citations, docs); other schemes are not.
      const refsAfter = await extractExternalReferences(zip);
      assertNoNewExternalReferences(refsBefore, refsAfter, { allowHttpReferences });

      const packageAfter = requireWriteVerification
        ? await snapshotZipEntries(zip)
        : null;
      const changedPaths = requireWriteVerification
        ? changedZipEntryPaths(packageBefore, packageAfter)
        : [];
      if (requireWriteVerification && changedPaths.length === 0) {
        throw new Error('Private PowerPoint write made no package change');
      }

      // Step 6: Generate modified base64 and reimport (SAFE ORDER: insert first, then delete)
      const modifiedBase64 = await zip.generateAsync({ type: 'base64' });

      // Insert modified slide FIRST (safe: if insert fails, original is preserved)
      context.presentation.insertSlidesFromBase64(modifiedBase64, {
        targetSlideId: slideIndex > 0
          ? slides.items[slideIndex - 1].id   // Insert after previous slide
          : slide.id                           // Slide 0: insert after original, then delete
      });
      // Delete the original slide
      slide.delete();
      await context.sync();

      // Step 7: Restore slide selection (1.3)
      slides.load('items/id');
      await context.sync();
      if (requireWriteVerification) {
        const replacementSlide = slides.items[slideIndex];
        if (!replacementSlide) {
          throw new Error('Private PowerPoint write target was not reinserted');
        }
        const readbackBase64 = replacementSlide.exportAsBase64();
        await context.sync();
        const readbackZip = await JSZip.loadAsync(readbackBase64.value, { base64: true });
        const readbackPackage = await snapshotZipEntries(readbackZip);
        assertChangedZipEntriesSurvived(packageAfter, readbackPackage, changedPaths);
      }
      try {
        const selectedIds = selectedIndices
          .map((idx) => slides.items[idx]?.id)
          .filter((id) => typeof id === 'string' && id.length > 0);
        if (selectedIds.length > 0) {
          context.presentation.setSelectedSlides(selectedIds);
          await context.sync();
        }
      } catch {
        // Selection restore is best-effort — don't crash the operation
      }

      if (requireWriteVerification) {
        return {
          success: true,
          nativeMutationApplied: true,
          slideIndex,
          verifiedChangedPartCount: changedPaths.length,
        };
      }
      return callbackResult ?? 'Slide modified successfully';
    }, { timeoutMs: TOOL_TIMEOUT_MS, label: 'OOXML edit', signal });
  }

  /**
   * OOXML manipulation for slide master/theme.
   * Exports slide 0 as full PPTX, modifies master/theme/layout files.
   *
   * @param {string} code - Async function body receiving { zip, markDirty }
   * @returns {Promise<string>} - Result description
   */
  async withMasterZip(code) {
    if (typeof PowerPoint === 'undefined') {
      throw new Error('PowerPoint API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      if (slides.items.length === 0) {
        throw new Error('Presentation has no slides');
      }

      // Export first slide (contains master/theme data)
      const slide = slides.items[0];
      const base64Result = slide.exportAsBase64();
      await context.sync();
      const base64Data = base64Result.value;

      // Create JSZip archive
      const zip = await JSZip.loadAsync(base64Data, { base64: true });

      // Snapshot external references BEFORE callback
      const refsBefore = await extractExternalReferences(zip);

      // Run callback
      let isDirty = false;
      const markDirty = () => { isDirty = true; };

      let callbackResult;
      try {
        callbackResult = await sandboxedEval(code, {
          zip, markDirty, escapeXml, DOMParser, XMLSerializer, findShapeByName,
        });
      } catch (sesError) {
        if (isSesInitializationError(sesError)) {
          throw createSesUnavailableError('edit_slide_master');
        }
        throw sesError;
      }

      if (!isDirty) {
        return callbackResult ?? 'No modifications made (markDirty() not called)';
      }

      // Sanitize XML
      for (const [path, file] of Object.entries(zip.files)) {
        if (path.endsWith('.xml') && !file.dir) {
          const content = await file.async('string');
          const sanitized = sanitizeXmlAmpersands(content);
          if (sanitized !== content) {
            zip.file(path, sanitized);
          }
        }
      }

      // Check for NEW external references (before/after comparison).
      // http(s) URLs are permitted; other schemes (file://, data:, javascript:, etc.) are not.
      const refsAfter = await extractExternalReferences(zip);
      for (const ref of refsAfter) {
        if (refsBefore.has(ref)) continue;
        if (isSafeExternalReference(ref)) continue;
        throw new Error(`Security: blocked unsafe external reference: ${ref}`);
      }

      // Reimport — SAFE ORDER: insert first, then delete
      const modifiedBase64 = await zip.generateAsync({ type: 'base64' });

      // Insert modified slide FIRST (safe: if insert fails, original is preserved)
      context.presentation.insertSlidesFromBase64(modifiedBase64, {
        targetSlideId: slide.id  // Insert after original, then delete original
      });
      slide.delete();
      await context.sync();

      // Clean up duplicate/orphaned slide masters from reimport
      await this._cleanupSlideMasters(context);

      return callbackResult ?? 'Master/theme modified successfully';
    }, { timeoutMs: TOOL_TIMEOUT_MS, label: 'edit_slide_master' });
  }

  // ===========================================================================
  // TOOL: edit_slide_xml
  // ===========================================================================

  /**
   * Edit raw OOXML of a slide using JSZip.
   * @param {number} slideIndex - 0-based slide index
   * @param {string} code - Async function body receiving { zip, markDirty }
   * @returns {Promise<string>}
   */
  async editSlideXml(slideIndex, code, options = {}) {
    return this.withSlideZip(slideIndex, code, options);
  }

  // ===========================================================================
  // TOOL: edit_slide_chart
  // ===========================================================================

  /**
   * Add or edit charts via OOXML manipulation.
   * Same JSZip mechanism as editSlideXml but specifically for chart creation.
   * @param {number} slideIndex - 0-based slide index
   * @param {string} code - Async function body receiving { zip, markDirty }
   * @returns {Promise<string>}
   */
  async editSlideChart(slideIndex, code, options = {}) {
    return this.withSlideZip(slideIndex, code, options);
  }

  // ===========================================================================
  // TOOL: edit_slide_master
  // ===========================================================================

  /**
   * Edit slide master and theme via OOXML.
   * Exports full PPTX structure including slideMasters, slideLayouts, theme.
   * @param {string} code - Async function body receiving { zip, markDirty }
   * @returns {Promise<string>}
   */
  async editSlideMaster(code) {
    return this.withMasterZip(code);
  }

  /**
   * Clean up duplicate/orphaned slide masters after withMasterZip reimport.
   * Rebind slides to the primary master and force-remove orphaned masters.
   * @param {Object} context - PowerPoint.RequestContext
   */
  async _cleanupSlideMasters(context) {
    try {
      const masters = context.presentation.slideMasters;
      const slides = context.presentation.slides;
      masters.load('items');
      slides.load('items');
      await context.sync();

      if (masters.items.length <= 1) return;

      for (const master of masters.items) {
        master.layouts.load('items/id,items/name');
      }
      for (const slide of slides.items) {
        slide.layout.load('id,name');
      }
      await context.sync();

      const primaryLayoutId = slides.items[0]?.layout?.id;
      const primaryMaster = masters.items.find((master) =>
        master.layouts.items.some((layout) => layout.id === primaryLayoutId)
      );
      if (!primaryMaster) return;

      for (let i = 1; i < slides.items.length; i++) {
        const slide = slides.items[i];
        const matchingLayout = primaryMaster.layouts.items.find(
          (layout) => layout.name === slide.layout.name
        );
        if (matchingLayout) {
          slide.applyLayout(matchingLayout);
        }
      }
      await context.sync();

      const orphanedMasters = masters.items.filter((master) => master !== primaryMaster);
      for (const master of orphanedMasters) {
        await this._forceRemoveMaster(context, master);
      }
    } catch (e) {
      // Cleanup is best-effort — don't crash the operation
      console.warn('[PowerPointBridge] Master cleanup skipped:', e.message);
    }
  }

  async _forceRemoveMaster(context, master) {
    const firstLayout = master.layouts.items[0];
    if (!firstLayout) return;

    context.presentation.slides.add({ layoutId: firstLayout.id });
    await context.sync();

    const slides = context.presentation.slides;
    slides.load('items');
    await context.sync();

    slides.items[slides.items.length - 1]?.delete();
    await context.sync();
  }

  // ===========================================================================
  // TOOL: read_slide_text
  // ===========================================================================

  /**
   * Read raw OOXML paragraph XML from a shape's text body.
   * Returns <a:p> elements as a string, preserving all formatting.
   *
   * @param {number} slideIndex - 0-based slide index
   * @param {string} shapeId - Target shape ID (locale-independent)
   * @returns {Promise<string>} - OOXML paragraph XML
   */
  async readSlideText(slideIndex, shapeId, options = {}) {
    if (typeof PowerPoint === 'undefined') {
      throw new Error('PowerPoint API not available');
    }
    if (!shapeId) {
      throw new Error('shape_id is required');
    }

    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      if (slideIndex < 0 || slideIndex >= slides.items.length) {
        throw new Error(`Slide index ${slideIndex} out of range (0-${slides.items.length - 1})`);
      }

      const slide = slides.items[slideIndex];

      // Export slide as base64 to get OOXML
      const base64Result = slide.exportAsBase64();
      await context.sync();

      const zip = await JSZip.loadAsync(base64Result.value, { base64: true });

      // Read the slide XML (always slide1.xml inside the exported ZIP)
      const slideXml = await zip.file('ppt/slides/slide1.xml').async('string');
      const doc = parseXmlSafe(slideXml, 'slide1.xml');

      // Find shape by ID
      const spNodes = doc.getElementsByTagNameNS(NS_P, 'sp');

      for (let i = 0; i < spNodes.length; i++) {
        const sp = spNodes[i];
        const nvSpPr = sp.getElementsByTagNameNS(NS_P, 'nvSpPr')[0];
        if (!nvSpPr) continue;

        const cNvPr = nvSpPr.getElementsByTagNameNS(NS_P, 'cNvPr')[0];
        if (!cNvPr) continue;

        if (cNvPr.getAttribute('id') !== shapeId) continue;

        // Found the target shape — extract <a:txBody> paragraph XML
        const txBody = sp.getElementsByTagNameNS(NS_P, 'txBody')[0];
        if (!txBody) {
          return '(empty — shape has no text body in the exported OOXML. This is normal for empty placeholders. You can still write to it with edit_slide_text.)';
        }

        const serializer = new XMLSerializer();
        const paragraphs = [];
        for (let j = 0; j < txBody.childNodes.length; j++) {
          const node = txBody.childNodes[j];
          if (node.nodeType === 1 && node.localName === 'p' && node.namespaceURI === NS_A) {
            paragraphs.push(serializer.serializeToString(node));
          }
        }

        return paragraphs.length === 0
          ? '(empty — shape has a text body but no paragraph content. You can write to it with edit_slide_text.)'
          : paragraphs.join('\n');
      }

      throw new Error(`Shape ID "${shapeId}" not found on slide ${slideIndex + 1}`);
    }, options);
  }

  // ===========================================================================
  // TOOL: edit_slide_text
  // ===========================================================================

  /**
   * Replace paragraph content of a shape with raw OOXML <a:p> XML.
   * Preserves the shape's <a:bodyPr> and <a:lstStyle>.
   *
   * @param {number} slideIndex - 0-based slide index
   * @param {string} shapeId - Target shape ID (locale-independent)
   * @param {string} paragraphXml - Raw OOXML <a:p> paragraph XML
   * @returns {Promise<string>}
   */
  async editSlideText(slideIndex, shapeId, paragraphXml, options = {}) {
    if (!shapeId) {
      throw new Error('shape_id is required');
    }
    // 1.6: Sanitize bare ampersands in LLM-generated paragraph XML before parsing
    const sanitizedParagraphXml = sanitizeXmlAmpersands(paragraphXml);

    // 1.5: Validate the new paragraph XML parses correctly before modifying the slide
    const validationXml = '<root xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">' +
      sanitizedParagraphXml + '</root>';
    const validationDoc = parseXmlSafe(validationXml, 'new paragraph XML');
    const validPs = validationDoc.getElementsByTagNameNS(NS_A, 'p');
    if (validPs.length === 0) {
      throw new Error('paragraphXml must contain at least one <a:p> element');
    }

    const lookupLabel = `ID "${shapeId}"`;

    const code = `
      const NS_P = 'http://schemas.openxmlformats.org/presentationml/2006/main';
      const NS_A = 'http://schemas.openxmlformats.org/drawingml/2006/main';
      const slideXml = await zip.file('ppt/slides/slide1.xml').async('string');
      const doc = (() => {
        const d = new DOMParser().parseFromString(slideXml, 'application/xml');
        const errs = d.getElementsByTagName('parsererror');
        if (errs.length > 0) throw new Error('Malformed slide XML: ' + (errs[0].textContent || '').slice(0, 200));
        return d;
      })();

      const spNodes = doc.getElementsByTagNameNS(NS_P, 'sp');
      let found = false;

      for (let i = 0; i < spNodes.length; i++) {
        const sp = spNodes[i];
        const nvSpPr = sp.getElementsByTagNameNS(NS_P, 'nvSpPr')[0];
        if (!nvSpPr) continue;
        const cNvPr = nvSpPr.getElementsByTagNameNS(NS_P, 'cNvPr')[0];
        if (!cNvPr) continue;
        if (cNvPr.getAttribute('id') !== ${JSON.stringify(shapeId)}) continue;

        // Found target shape — get or create txBody (1.4: handle empty placeholders)
        let txBody = sp.getElementsByTagNameNS(NS_P, 'txBody')[0];
        if (!txBody) {
          // Create txBody for empty placeholder shapes
          const txBodyXml = '<p:txBody xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:bodyPr/><a:lstStyle/></p:txBody>';
          const txBodyDoc = new DOMParser().parseFromString(txBodyXml, 'application/xml');
          txBody = doc.importNode(txBodyDoc.documentElement, true);
          sp.appendChild(txBody);
        }

        // Remove all existing <a:p> elements
        const existingPs = Array.from(txBody.childNodes).filter((node) =>
          node.nodeType === 1 && node.namespaceURI === NS_A && node.localName === 'p'
        );
        existingPs.forEach(p => txBody.removeChild(p));

        // Parse and insert new paragraph XML (already sanitized + validated)
        const newXml = '<root xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">' +
          ${JSON.stringify(sanitizedParagraphXml)} + '</root>';
        const newDoc = new DOMParser().parseFromString(newXml, 'application/xml');
        const errs = newDoc.getElementsByTagName('parsererror');
        if (errs.length > 0) throw new Error('Invalid XML: ' + (errs[0].textContent || '').slice(0, 200));

        let paragraphCount = 0;
        for (let j = 0; j < newDoc.documentElement.childNodes.length; j++) {
          const child = newDoc.documentElement.childNodes[j];
          if (child.nodeType === 1) {
            if (child.localName !== 'p' || child.namespaceURI !== NS_A) {
              throw new Error('Invalid element <' + child.nodeName + '> — only <a:p> paragraph elements are allowed');
            }
            txBody.appendChild(doc.importNode(child, true));
            paragraphCount++;
          }
        }
        if (paragraphCount === 0) throw new Error('paragraphXml must contain at least one <a:p> element');

        found = true;
        break;
      }

      if (!found) throw new Error('Shape ${lookupLabel} not found');

      const serializer = new XMLSerializer();
      zip.file('ppt/slides/slide1.xml', serializer.serializeToString(doc));
      markDirty();
      return 'Text replaced successfully';
    `;

    return this.withSlideZip(slideIndex, code, options);
  }

  // ===========================================================================
  // TOOL: duplicate_slide
  // ===========================================================================

  /**
   * Duplicate a slide. The copy is inserted immediately after the original.
   * @param {number} slideIndex - 0-based slide index
   * @returns {Promise<string>}
   */
  async duplicateSlide(slideIndex) {
    if (typeof PowerPoint === 'undefined') {
      throw new Error('PowerPoint API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      if (slideIndex < 0 || slideIndex >= slides.items.length) {
        throw new Error(`Slide index ${slideIndex} out of range (0-${slides.items.length - 1})`);
      }

      const slide = slides.items[slideIndex];

      // Export the slide and reimport it after the original
      const base64Result = slide.exportAsBase64();
      await context.sync();

      context.presentation.insertSlidesFromBase64(base64Result.value, {
        targetSlideId: slide.id
      });
      await context.sync();

      return `Slide ${slideIndex + 1} duplicated`;
    });
  }

  // ===========================================================================
  // TOOL: insert_icon
  // ===========================================================================

  /**
   * Insert an icon onto a slide as a vector SVG with a PNG fallback.
   * The SVG content arrives from the backend (which owns the icon library);
   * this leg recolors, rasterizes the fallback, and embeds both via OOXML
   * (<asvg:svgBlip> + <a:blip>) inside a <p:pic> appended to the shape tree.
   *
   * @param {Object} params
   * @param {number} params.slide_index - 0-based slide index
   * @param {string} params.svg - Raw SVG markup
   * @param {number} [params.x] - Left in points (default: centered)
   * @param {number} [params.y] - Top in points (default: centered)
   * @param {number} [params.width=72] - Width in points
   * @param {number} [params.height=72] - Height in points
   * @param {string} [params.color] - Hex recolor (e.g. "#B83B1D")
   * @param {string} [params.description] - Alt text
   */
  async insertIcon({ slide_index, svg, x, y, width, height, color, description }) {
    if (typeof svg !== 'string' || !svg.includes('<svg')) {
      throw new Error('insert_icon: missing or invalid svg content');
    }

    const w = Number.isFinite(width) ? width : 72;
    const h = Number.isFinite(height) ? height : 72;
    // Default placement: centered on a standard 16:9 slide (960x540 pt).
    const px = Number.isFinite(x) ? x : (960 - w) / 2;
    const py = Number.isFinite(y) ? y : (540 - h) / 2;
    const descr = typeof description === 'string' ? description : '';

    const svgToUse = color ? recolorSvg(svg, color) : svg;
    const pngBase64 = await rasterizeSvgToPng(svgToUse, ICON_PNG_SIZE, ICON_PNG_SIZE);

    return this._withSlideZipNative(slide_index, async ({ zip, markDirty }) => {
      const mediaCount = Object.keys(zip.files).filter((f) => f.startsWith('ppt/media/')).length + 1;
      const svgPath = `ppt/media/icon${mediaCount}.svg`;
      const pngPath = `ppt/media/icon${mediaCount}.png`;

      zip.file(svgPath, svgToUse);
      zip.file(pngPath, pngBase64, { base64: true });
      await registerContentType(zip, 'svg', 'image/svg+xml');
      await registerContentType(zip, 'png', 'image/png');

      // Register both media parts as image relationships on the slide.
      const relsPath = 'ppt/slides/_rels/slide1.xml.rels';
      const relsFile = zip.file(relsPath);
      const relsXml = relsFile
        ? await relsFile.async('string')
        : '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"/>';
      const relsDoc = new DOMParser().parseFromString(relsXml, 'application/xml');
      const relsNs = 'http://schemas.openxmlformats.org/package/2006/relationships';
      const imgType = `${NS_REL}/image`;
      const rels = relsDoc.getElementsByTagName('Relationship');
      let maxId = 0;
      for (let i = 0; i < rels.length; i++) {
        const n = parseInt((rels[i].getAttribute('Id') || '').replace(/\D/g, ''), 10);
        if (!Number.isNaN(n) && n > maxId) maxId = n;
      }
      const rIdPng = `rId${maxId + 1}`;
      const rIdSvg = `rId${maxId + 2}`;

      const relPng = relsDoc.createElementNS(relsNs, 'Relationship');
      relPng.setAttribute('Id', rIdPng);
      relPng.setAttribute('Type', imgType);
      relPng.setAttribute('Target', `../media/icon${mediaCount}.png`);
      relsDoc.documentElement.appendChild(relPng);

      const relSvg = relsDoc.createElementNS(relsNs, 'Relationship');
      relSvg.setAttribute('Id', rIdSvg);
      relSvg.setAttribute('Type', imgType);
      relSvg.setAttribute('Target', `../media/icon${mediaCount}.svg`);
      relsDoc.documentElement.appendChild(relSvg);
      zip.file(relsPath, new XMLSerializer().serializeToString(relsDoc));

      // Append the <p:pic> to the slide's shape tree.
      const slideXml = await zip.file('ppt/slides/slide1.xml').async('string');
      const slideDoc = new DOMParser().parseFromString(slideXml, 'application/xml');
      const spTree = slideDoc.getElementsByTagNameNS(NS_P, 'spTree')[0];
      if (!spTree) throw new Error('Could not find <p:spTree> in slide XML');

      let maxShapeId = 0;
      for (const list of [
        slideDoc.getElementsByTagNameNS(NS_P, 'cNvPr'),
        slideDoc.getElementsByTagNameNS(NS_A, 'cNvPr')
      ]) {
        for (let i = 0; i < list.length; i++) {
          const n = parseInt(list[i].getAttribute('id'), 10);
          if (!Number.isNaN(n) && n > maxShapeId) maxShapeId = n;
        }
      }
      const shapeId = maxShapeId + 1;

      const offX = Math.round(px * EMU_PER_PT);
      const offY = Math.round(py * EMU_PER_PT);
      const extCx = Math.round(w * EMU_PER_PT);
      const extCy = Math.round(h * EMU_PER_PT);

      const picXml = `<p:pic xmlns:p="${NS_P}" xmlns:a="${NS_A}" xmlns:r="${NS_REL}">
        <p:nvPicPr>
          <p:cNvPr id="${shapeId}" name="Graphic ${shapeId}" descr="${escapeXml(descr)}"/>
          <p:cNvPicPr><a:picLocks noChangeAspect="1"/></p:cNvPicPr>
          <p:nvPr/>
        </p:nvPicPr>
        <p:blipFill>
          <a:blip r:embed="${rIdPng}">
            <a:extLst>
              <a:ext uri="{96DAC541-7B7A-43D3-8B79-37D633B846F1}">
                <asvg:svgBlip xmlns:asvg="http://schemas.microsoft.com/office/drawing/2016/SVG/main" r:embed="${rIdSvg}"/>
              </a:ext>
            </a:extLst>
          </a:blip>
          <a:stretch><a:fillRect/></a:stretch>
        </p:blipFill>
        <p:spPr>
          <a:xfrm>
            <a:off x="${offX}" y="${offY}"/>
            <a:ext cx="${extCx}" cy="${extCy}"/>
          </a:xfrm>
          <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
        </p:spPr>
      </p:pic>`;

      const picDoc = new DOMParser().parseFromString(picXml, 'application/xml');
      if (picDoc.getElementsByTagName('parsererror').length > 0) {
        throw new Error('insert_icon: generated <p:pic> XML failed to parse');
      }
      spTree.appendChild(slideDoc.importNode(picDoc.documentElement, true));
      zip.file('ppt/slides/slide1.xml', new XMLSerializer().serializeToString(slideDoc));
      markDirty();

      return { inserted: true, shapeId: String(shapeId), x: px, y: py, width: w, height: h };
    });
  }

  /**
   * Attach code-issued Daloopa source markers to an explicit target slide. The marker
   * block is native slide text, is idempotent, and is read back before the
   * bridge acknowledges success.
   */
  async attachConnectorCitations({ citations, target }, options = {}) {
    const safeCitations = validateDaloopaCitations(citations);
    const slideIndex = validateDaloopaPowerPointTarget(target);
    if (typeof PowerPoint === 'undefined') {
      throw new Error('PowerPoint API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const presentation = context.presentation;
      const slides = presentation.slides;
      slides.load('items/id');
      await context.sync();

      let slideWidth = 960;
      let slideHeight = 540;
      try {
        presentation.load('slideWidth,slideHeight');
        await context.sync();
        slideWidth = Number(presentation.slideWidth) || slideWidth;
        slideHeight = Number(presentation.slideHeight) || slideHeight;
      } catch {
        // Older hosts use the standard fallback dimensions.
      }

      if (slideIndex >= slides.items.length) {
        throw new Error('The PowerPoint citation target slide does not exist');
      }
      const targetSlide = slides.items[slideIndex];
      const shapes = targetSlide.shapes;
      shapes.load('items/name');
      await context.sync();

      let sourceShape = shapes.items.find((shape) => shape.name === DALOOPA_CITATION_SHAPE_NAME) || null;
      let existingText = '';
      if (sourceShape) {
        const existingRange = sourceShape.textFrame.textRange;
        existingRange.load('text');
        await context.sync();
        existingText = String(existingRange.text || '');
      }

      const missingMarkers = safeCitations
        .map((citation) => citation.marker)
        .filter((marker) => !existingText.includes(marker));
      const nextText = [existingText.trim(), ...missingMarkers].filter(Boolean).join('\r');
      if (nextText.length > 32_000) {
        throw new Error('The PowerPoint Daloopa source block is full');
      }

      const markerLineCount = Math.max(1, nextText.split(/\r|\n/).filter(Boolean).length);
      const sourceHeight = Math.min(72, Math.max(20, markerLineCount * 10 + 8));
      const sourceGeometry = {
        left: 18,
        top: Math.max(0, slideHeight - sourceHeight - 18),
        width: Math.max(100, slideWidth - 36),
        height: sourceHeight,
      };
      if (sourceShape) {
        sourceShape.delete();
        await context.sync();
      }
      sourceShape = shapes.addTextBox(nextText, sourceGeometry);

      const verificationRange = sourceShape.textFrame.textRange;
      sourceShape.name = DALOOPA_CITATION_SHAPE_NAME;
      sourceShape.left = sourceGeometry.left;
      sourceShape.top = sourceGeometry.top;
      sourceShape.width = sourceGeometry.width;
      sourceShape.height = sourceGeometry.height;
      sourceShape.textFrame.autoSizeSetting =
        PowerPoint.ShapeAutoSize?.autoSizeTextToFitShape || 'AutoSizeTextToFitShape';
      verificationRange.font.size = 7;
      verificationRange.font.color = '#595959';
      verificationRange.font.bold = false;
      verificationRange.font.italic = false;
      sourceShape.load('name,left,top,width,height');
      verificationRange.load('text');
      verificationRange.font.load('size,color,bold,italic');
      await context.sync();
      const verifiedText = String(verificationRange.text || '');
      const geometryVerified = sourceShape.left === sourceGeometry.left &&
        sourceShape.top === sourceGeometry.top &&
        sourceShape.width === sourceGeometry.width &&
        sourceShape.height === sourceGeometry.height;
      const fontVerified = verificationRange.font.size === 7 &&
        String(verificationRange.font.color || '').toUpperCase() === '#595959' &&
        verificationRange.font.bold === false &&
        verificationRange.font.italic === false;
      if (
        !geometryVerified ||
        !fontVerified ||
        safeCitations.some((citation) => !verifiedText.includes(citation.marker))
      ) {
        throw new Error('Daloopa source marker verification failed in PowerPoint');
      }

      return {
        success: true,
        attachedCitationIds: safeCitations.map((citation) => citation.citationId),
      };
    }, { label: 'attach_connector_citations', ...options });
  }

  // ===========================================================================
  // TOOL: verify_slides
  // ===========================================================================

  /**
   * Verify all slides for overlapping shapes, out-of-bounds positioning,
   * and unused placeholders. Returns structured results per slide.
   * @returns {Promise<Object>} - Verification results
   */
  async verifySlides() {
    if (typeof PowerPoint === 'undefined') {
      throw new Error('PowerPoint API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      // 2.1: Read actual slide dimensions instead of hardcoded values
      let SLIDE_WIDTH = 960;
      let SLIDE_HEIGHT = 540;
      try {
        context.presentation.load('slideWidth,slideHeight');
        await context.sync();
        SLIDE_WIDTH = context.presentation.slideWidth || 960;
        SLIDE_HEIGHT = context.presentation.slideHeight || 540;
      } catch {
        // Dimensions API not available on all platforms — use defaults
      }

      const results = [];

      for (let i = 0; i < slides.items.length; i++) {
        const slide = slides.items[i];
        const shapes = slide.shapes;
        // 2.2: Also load shape id
        shapes.load('items/id,items/name,items/type,items/left,items/top,items/width,items/height');
        await context.sync();

        const slideResult = {
          slideIndex: i,
          slideNumber: i + 1,
          shapes: [],
          issues: [],
          shapeCount: shapes.items.length,
        };

        const shapeBounds = [];

        for (const shape of shapes.items) {
          const bounds = {
            id: String(shape.id),
            name: shape.name,
            type: shape.type,
            left: shape.left,
            top: shape.top,
            right: shape.left + shape.width,
            bottom: shape.top + shape.height,
            width: shape.width,
            height: shape.height,
          };
          shapeBounds.push(bounds);
          slideResult.shapes.push({
            id: bounds.id,
            name: bounds.name,
            type: bounds.type,
            left: bounds.left,
            top: bounds.top,
            width: bounds.width,
            height: bounds.height,
          });

          // Check out-of-bounds
          if (bounds.left < 0 || bounds.top < 0 ||
              bounds.right > SLIDE_WIDTH || bounds.bottom > SLIDE_HEIGHT) {
            slideResult.issues.push({
              type: 'out_of_bounds',
              shape: { id: bounds.id, name: bounds.name },
              details: {
                left: bounds.left,
                top: bounds.top,
                right: bounds.right,
                bottom: bounds.bottom,
                slideWidth: SLIDE_WIDTH,
                slideHeight: SLIDE_HEIGHT,
              },
            });
          }

          // Check for zero-dimension shapes (likely unused placeholders)
          if (shape.width === 0 || shape.height === 0) {
            slideResult.issues.push({
              type: 'zero_dimension',
              shape: { id: bounds.id, name: bounds.name },
            });
          }
        }

        // Check for overlapping shapes (AABB overlap detection)
        for (let a = 0; a < shapeBounds.length; a++) {
          for (let b = a + 1; b < shapeBounds.length; b++) {
            const sa = shapeBounds[a];
            const sb = shapeBounds[b];

            // AABB overlap test
            if (sa.left < sb.right && sa.right > sb.left &&
                sa.top < sb.bottom && sa.bottom > sb.top) {
              // Calculate overlap area
              const overlapLeft = Math.max(sa.left, sb.left);
              const overlapTop = Math.max(sa.top, sb.top);
              const overlapRight = Math.min(sa.right, sb.right);
              const overlapBottom = Math.min(sa.bottom, sb.bottom);
              const overlapArea = (overlapRight - overlapLeft) * (overlapBottom - overlapTop);

              // Only report significant overlaps (> 100 sq points)
              if (overlapArea > 100) {
                slideResult.issues.push({
                  type: 'overlap',
                  shapes: [
                    { id: sa.id, name: sa.name },
                    { id: sb.id, name: sb.name },
                  ],
                  overlapArea: Math.round(overlapArea),
                });
              }
            }
          }
        }

        // Check for unused placeholders (shapes with placeholder-like names but no text)
        for (const shape of shapes.items) {
          if (/placeholder/i.test(shape.name) || /^(Title|Subtitle|Content|Text)\s/i.test(shape.name)) {
            try {
              const textRange = shape.textFrame.textRange;
              textRange.load('text');
              await context.sync();
              if (!textRange.text || textRange.text.trim() === '') {
                slideResult.issues.push({
                  type: 'unused_placeholder',
                  shape: { id: String(shape.id), name: shape.name },
                });
              }
            } catch {
              // No text frame — not a text placeholder
            }
          }
        }

        results.push(slideResult);
      }

      return {
        totalSlides: slides.items.length,
        slidesWithIssues: results.filter(r => r.issues.length > 0).length,
        results,
      };
    });
  }

  // ===========================================================================
  // TOOL: read_deck_content
  // ===========================================================================

  /**
   * Read content of ALL slides — Mac-safe version.
   * Uses try/catch .textFrame.textRange pattern (not getTextFrameOrNullObject
   * which throws GeneralException on Mac desktop).
   *
   * @returns {Promise<Object>} - { totalSlides, slides: [{ slideNumber, slideIndex, shapes }] }
   */
  async readDeckContent() {
    if (typeof PowerPoint === 'undefined') {
      throw new Error('PowerPoint API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      const slidesData = [];

      for (let i = 0; i < slides.items.length; i++) {
        const slide = slides.items[i];
        const shapes = slide.shapes;
        shapes.load('items/id,items/name,items/type,items/left,items/top,items/width,items/height');
        await context.sync();

        const shapesList = [];

        for (const shape of shapes.items) {
          const info = {
            id: String(shape.id),
            name: shape.name,
            type: shape.type,
            position: {
              left: shape.left,
              top: shape.top,
              width: shape.width,
              height: shape.height,
            },
          };

          // Mac-safe text reading: try/catch on .textFrame (NOT getTextFrameOrNullObject)
          try {
            const textRange = shape.textFrame.textRange;
            textRange.load('text');
            await context.sync();
            info.text = textRange.text;
          } catch {
            // 2.3: Helpful message for shapes without text frames
            const t = String(shape.type).toLowerCase();
            if (t.includes('picture') || t.includes('image')) {
              info.note = '[Image - no text]';
            } else if (t.includes('chart')) {
              info.note = '[Chart - use edit_slide_chart to modify]';
            } else if (t.includes('table')) {
              info.note = '[Table - no text frame]';
            } else if (t.includes('connector') || t.includes('line')) {
              info.note = '[Connector/Line]';
            }
          }

          shapesList.push(info);
        }

        slidesData.push({
          slideNumber: i + 1,
          slideIndex: i,
          shapes: shapesList,
        });
      }

      return {
        totalSlides: slides.items.length,
        slides: slidesData,
      };
    });
  }

  // ===========================================================================
  // TOOL: list_slide_shapes
  // ===========================================================================

  /**
   * List all shapes on a slide with their IDs, names, types, and positions.
   * Shape IDs are stable and locale-independent (unlike shape names).
   * @param {number} slideIndex - 0-based slide index
   * @returns {Promise<Array>} - Array of shape info objects
   */
  async listSlideShapes(slideIndex, options = {}) {
    if (typeof PowerPoint === 'undefined') {
      throw new Error('PowerPoint API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      if (slideIndex < 0 || slideIndex >= slides.items.length) {
        throw new Error(`Slide index ${slideIndex} out of range (0-${slides.items.length - 1})`);
      }

      const shapes = slides.items[slideIndex].shapes;
      shapes.load('items/id,items/name,items/type,items/left,items/top,items/width,items/height');
      await context.sync();

      const result = [];
      for (const shape of shapes.items) {
        const info = {
          id: String(shape.id),
          name: shape.name,
          type: shape.type,
          left: shape.left,
          top: shape.top,
          width: shape.width,
          height: shape.height,
        };

        // Try to detect if shape has text
        try {
          const textRange = shape.textFrame.textRange;
          textRange.load('text');
          await context.sync();
          info.hasText = true;
          info.textPreview = textRange.text?.substring(0, 80) || '';
        } catch {
          info.hasText = false;
        }

        result.push(info);
      }

      return result;
    }, options);
  }

  // ===========================================================================
  // CONTEXT: getInitialState (deck metadata for context injection)
  // ===========================================================================

  /**
   * Gather deck metadata sent with every user message as <initial_state>.
   * @returns {Promise<Object>} - Deck metadata
   */
  async getInitialState() {
    if (typeof PowerPoint === 'undefined') {
      return null;
    }

    try {
      return await this._safeOfficeRun(async (context) => {
        const presentation = context.presentation;
        const slides = presentation.slides;
        const masters = presentation.slideMasters;

        slides.load('items/id');
        masters.load('items/name,items/id');
        await context.sync();

        // Get slide dimensions
        let slideWidth = 960;
        let slideHeight = 540;
        try {
          presentation.load('slideWidth,slideHeight');
          await context.sync();
          slideWidth = presentation.slideWidth || 960;
          slideHeight = presentation.slideHeight || 540;
        } catch {
          // Dimensions not available on all platforms
        }

        // Build masters with layouts
        const mastersData = [];
        for (const master of masters.items) {
          const layouts = master.layouts;
          layouts.load('items/id,items/name');
          await context.sync();

          mastersData.push({
            id: master.id,
            name: master.name,
            layouts: layouts.items.map(l => ({ id: l.id, name: l.name })),
          });
        }

        // Build slides metadata
        const slidesMetadata = slides.items.map((slide, idx) => ({
          slideId: slide.id,
          position: idx + 1,
        }));

        // Detect if using default theme
        const isDefaultTheme = mastersData.length > 0 &&
          /^(Office Theme|Blank|Default)$/i.test(mastersData[0]?.name || '');

        // Check if presentation has content
        let hasContent = false;
        if (slides.items.length > 0) {
          try {
            const firstSlide = slides.items[0];
            const shapes = firstSlide.shapes;
            shapes.load('items');
            await context.sync();
            hasContent = shapes.items.length > 0;
          } catch {
            hasContent = slides.items.length > 1;
          }
        }

        const docUrl = (typeof Office !== 'undefined' && Office.context?.document?.url) || '';
        const fileName = docUrl ? (docUrl.split('/').pop().split('?')[0] || 'Presentation') : 'Presentation';

        return {
          fileName,
          masters: mastersData,
          slidesMetadata,
          totalSlides: slides.items.length,
          slideWidth,
          slideHeight,
          displayLanguage: typeof Office !== 'undefined' ? (Office.context?.displayLanguage || null) : null,
          isDefaultTheme,
          hasContent,
        };
      });
    } catch (error) {
      console.warn('[PowerPointBridge] getInitialState failed:', error.message);
      return null;
    }
  }

  // ===========================================================================
  // CONTEXT: getUserContext (current selection)
  // ===========================================================================

  /**
   * Get current user selection state for <user_context> injection.
   * @returns {Promise<Object>} - Selection state
   */
  async getUserContext() {
    if (typeof PowerPoint === 'undefined') {
      return null;
    }

    // Office on the web: skip the selection probe. It is a non-essential
    // <user_context> nicety, but it runs through the SAME serialized
    // PowerPoint.run queue as the real tools. On the web host this probe's
    // PowerPoint.run can hang (observed: 120s timeout), and because runs are
    // serialized, that hang poisons the queue so every subsequent tool call
    // times out waiting behind it. Skipping it on the web gives the first real
    // tool run a clean, unblocked shot at the host.
    if (this._isOfficeOnline) {
      console.log('[PowerPointBridge] getUserContext skipped on Office web (avoids run-queue stall)');
      return null;
    }

    try {
      return await this._safeOfficeRun(async (context) => {
        const slides = context.presentation.slides;
        slides.load('items/id');
        await context.sync();

        // Get selected slides
        let selectedSlides = [];
        try {
          const selected = context.presentation.getSelectedSlides();
          selected.load('items/id');
          await context.sync();

          selectedSlides = selected.items.map(s => {
            const idx = slides.items.findIndex(sl => sl.id === s.id);
            return {
              slideId: s.id,
              positionOneIndexed: idx + 1,
            };
          });
        } catch {
          // getSelectedSlides not available on all platforms
        }

        return {
          selectedSlides,
          selectedShapeIds: [],
        };
      });
    } catch (error) {
      console.warn('[PowerPointBridge] getUserContext failed:', error.message);
      return null;
    }
  }

  // ===========================================================================
  // LEGACY METHODS (kept for backward compatibility)
  // ===========================================================================

  async readDeck() {
    if (typeof PowerPoint === 'undefined') {
      throw new Error('PowerPoint API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      const slideCount = slides.items.length;
      const designInfo = { slideCount, slides: [] };
      const samplesToRead = Math.min(slideCount, 3);

      for (let i = 0; i < samplesToRead; i++) {
        const slide = slides.items[i];
        const shapes = slide.shapes;
        shapes.load('items/name,items/type,items/left,items/top,items/width,items/height');
        await context.sync();

        const slideInfo = { index: i, shapes: [] };

        for (const shape of shapes.items) {
          const shapeInfo = {
            name: shape.name, type: shape.type,
            left: shape.left, top: shape.top,
            width: shape.width, height: shape.height,
          };

          try {
            const textRange = shape.textFrame.textRange;
            textRange.font.load('size,bold,color,name');
            await context.sync();
            shapeInfo.font = {
              size: textRange.font.size, bold: textRange.font.bold,
              color: textRange.font.color, name: textRange.font.name,
            };
            shapeInfo.text = textRange.text;
          } catch { /* no text frame */ }

          try {
            shape.fill.load('foregroundColor,type');
            await context.sync();
            shapeInfo.fill = { type: shape.fill.type, foregroundColor: shape.fill.foregroundColor };
          } catch { /* fill not readable */ }

          slideInfo.shapes.push(shapeInfo);
        }

        designInfo.slides.push(slideInfo);
      }

      return designInfo;
    });
  }

  async addSlide(options = {}) {
    return this._safeOfficeRun(async (context) => {
      const addOptions = options.layoutId ? { layoutId: options.layoutId } : {};
      context.presentation.slides.add(addOptions);
      await context.sync();
    });
  }

  async deleteSlide(index) {
    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();
      if (index < 0 || index >= slides.items.length) {
        throw new Error(`Slide index ${index} out of range (0-${slides.items.length - 1})`);
      }
      slides.items[index].delete();
      await context.sync();
    });
  }

  async getSlideCount() {
    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();
      return slides.items.length;
    });
  }

  async navigateToSlide(index) {
    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items/id');
      await context.sync();
      if (index < 0 || index >= slides.items.length) {
        throw new Error(`Slide index ${index} out of range (0-${slides.items.length - 1})`);
      }
      context.presentation.setSelectedSlides([slides.items[index].id]);
      await context.sync();
    });
  }

  async readSlide(index) {
    return this._safeOfficeRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();
      if (index < 0 || index >= slides.items.length) {
        throw new Error(`Slide index ${index} out of range (0-${slides.items.length - 1})`);
      }
      const slide = slides.items[index];
      const shapes = slide.shapes;
      shapes.load('items/name,items/type,items/left,items/top,items/width,items/height');
      await context.sync();

      const content = { index, shapes: [] };
      for (const shape of shapes.items) {
        const info = {
          name: shape.name, type: shape.type,
          left: shape.left, top: shape.top,
          width: shape.width, height: shape.height,
        };
        try {
          const textRange = shape.textFrame.textRange;
          textRange.load('text');
          textRange.font.load('size,bold,color,name');
          await context.sync();
          info.text = textRange.text;
          info.font = {
            size: textRange.font.size, bold: textRange.font.bold,
            color: textRange.font.color, name: textRange.font.name,
          };
        } catch { /* No text frame */ }
        content.shapes.push(info);
      }
      return content;
    });
  }

  /**
   * Import a template's primary master, layouts, and theme without retaining
   * its example-slide content. A single source slide is used as a temporary
   * carrier, then replaced by a clean slide on the imported layout.
   */
  async applyTemplateFromBase64(base64, options = {}) {
    if (typeof PowerPoint === 'undefined') {
      throw new Error('PowerPoint API not available');
    }
    const requirements = typeof Office !== 'undefined'
      ? Office?.context?.requirements
      : null;
    if (!requirements?.isSetSupported?.('PowerPointApi', '1.3')) {
      throw new Error('This PowerPoint version does not support applying a template design (requires PowerPointApi 1.3).');
    }
    // Slide.exportAsBase64 is a PowerPointApi 1.8 method. Template design
    // import itself works on 1.3, but removing the default starter slide must
    // stay fail-safe on older hosts because we cannot prove that it is blank.
    const supportsBlankStarterProbe = requirements.isSetSupported('PowerPointApi', '1.8');

    const [design, carrierBase64] = await Promise.all([
      analyzePowerPointTemplate(base64),
      createPowerPointTemplateCarrier(base64),
    ]);

    return this._safeOfficeRun(async (context) => {
      const presentation = context.presentation;
      const slides = presentation.slides;
      const tags = presentation.tags;
      slides.load('items/id');
      await context.sync();
      const originalSlides = [...slides.items];
      const originalSlideIds = new Set(originalSlides.map((slide) => slide.id));

      let existingTags = new Map();
      try {
        tags.load('items/key,items/value');
        await context.sync();
        existingTags = new Map(tags.items.map((tag) => [String(tag.key).toUpperCase(), tag.value]));
      } catch (tagReadError) {
        console.warn('[PowerPointBridge] Template idempotence tag read skipped:', tagReadError.message);
      }

      const storedFingerprint = existingTags.get(TEMPLATE_TAG_KEYS.fingerprint);
      const storedMasterId = existingTags.get(TEMPLATE_TAG_KEYS.masterId);
      const requestedApplyScope = String(options?.applyScope || '').trim() || null;
      if (storedFingerprint === design.fingerprint && storedMasterId) {
        const masters = presentation.slideMasters;
        masters.load('items/id,items/name');
        await context.sync();
        const existingMaster = masters.items.find((master) => master.id === storedMasterId);
        if (existingMaster) {
          existingMaster.layouts.load('items/id,items/name');
          await context.sync();
          const storedLayoutId = existingTags.get(TEMPLATE_TAG_KEYS.layoutId) || null;
          const storedWorkingSlideId = existingTags.get(TEMPLATE_TAG_KEYS.workingSlideId) || null;
          const storedApplyScope = existingTags.get(TEMPLATE_TAG_KEYS.applyScope) || null;
          const existingLayout = existingMaster.layouts.items.find((layout) => layout.id === storedLayoutId) ||
            existingMaster.layouts.items[0] || null;
          if (existingLayout) {
            // Retried delivery of the same tool call scope is safe and does not
            // leave an extra blank slide. A later turn gets a different scope
            // and must receive a fresh working slide to preserve prior content.
            if (
              requestedApplyScope &&
              requestedApplyScope === storedApplyScope &&
              storedWorkingSlideId &&
              originalSlideIds.has(storedWorkingSlideId)
            ) {
              try {
                presentation.setSelectedSlides([storedWorkingSlideId]);
                await context.sync();
              } catch (selectionError) {
                console.warn('[PowerPointBridge] Retried template working-slide selection skipped:', selectionError.message);
              }
              return {
                success: true,
                idempotent: true,
                designAlreadyApplied: true,
                templateFingerprint: design.fingerprint,
                sourceSlidesRetained: 0,
                createdSlides: 0,
                totalSlides: originalSlides.length,
                workingSlideId: storedWorkingSlideId,
                master: { id: existingMaster.id, name: existingMaster.name },
                workingLayout: { id: existingLayout.id, name: existingLayout.name },
                layouts: existingMaster.layouts.items.map((layout) => ({ id: layout.id, name: layout.name })),
                design,
                note: 'This template call was already applied for the current turn. The existing clean working slide was selected.',
              };
            }

            // The imported design is idempotent, but the working slide is not.
            // A prior turn may have filled the last tagged slide, so every new
            // apply_template invocation receives a fresh slide on the stored
            // layout. Reusing the old slide risks overwriting user content.
            let workingSlideId = null;
            try {
              slides.add({ slideMasterId: existingMaster.id, layoutId: existingLayout.id });
              await context.sync();
              slides.load('items/id');
              await context.sync();
              const cleanCandidates = slides.items.filter((slide) => !originalSlideIds.has(slide.id));
              if (cleanCandidates.length !== 1) {
                throw new Error(`Template working-slide creation produced ${cleanCandidates.length} slides instead of one`);
              }
              workingSlideId = cleanCandidates[0].id;
            } catch (creationError) {
              try {
                slides.load('items/id');
                await context.sync();
                for (const slide of slides.items) {
                  if (!originalSlideIds.has(slide.id)) slide.delete();
                }
                await context.sync();
              } catch (cleanupError) {
                console.error('[PowerPointBridge] Working-slide creation cleanup failed:', cleanupError.message);
              }
              throw creationError;
            }

            try {
              if (existingTags.has(TEMPLATE_TAG_KEYS.workingSlideId)) {
                tags.delete(TEMPLATE_TAG_KEYS.workingSlideId);
              }
              if (existingTags.has(TEMPLATE_TAG_KEYS.layoutId)) {
                tags.delete(TEMPLATE_TAG_KEYS.layoutId);
              }
              if (existingTags.has(TEMPLATE_TAG_KEYS.applyScope)) {
                tags.delete(TEMPLATE_TAG_KEYS.applyScope);
              }
              tags.add(TEMPLATE_TAG_KEYS.workingSlideId, workingSlideId);
              tags.add(TEMPLATE_TAG_KEYS.layoutId, existingLayout.id);
              if (requestedApplyScope) tags.add(TEMPLATE_TAG_KEYS.applyScope, requestedApplyScope);
              await context.sync();
            } catch (tagWriteError) {
              console.warn('[PowerPointBridge] Fresh working-slide tag write skipped:', tagWriteError.message);
            }

            try {
              presentation.setSelectedSlides([workingSlideId]);
              await context.sync();
            } catch (selectionError) {
              console.warn('[PowerPointBridge] Existing template working-slide selection skipped:', selectionError.message);
            }

            return {
              success: true,
              idempotent: false,
              designAlreadyApplied: true,
              templateFingerprint: design.fingerprint,
              sourceSlidesRetained: 0,
              createdSlides: 1,
              totalSlides: originalSlides.length + 1,
              workingSlideId,
              master: { id: existingMaster.id, name: existingMaster.name },
              workingLayout: { id: existingLayout.id, name: existingLayout.name },
              layouts: existingMaster.layouts.items.map((layout) => ({ id: layout.id, name: layout.name })),
              design,
              note: 'This template design was already available. A fresh clean working slide was created on its stored layout.',
            };
          }
        }
      }

      let blankStarter = false;
      const starterSlide = originalSlides.length === 1 ? originalSlides[0] : null;
      if (starterSlide && supportsBlankStarterProbe) {
        try {
          const starterExport = starterSlide.exportAsBase64();
          await context.sync();
          blankStarter = await isBlankPowerPointSlideExport(starterExport.value);
        } catch (starterProbeError) {
          console.warn('[PowerPointBridge] Blank starter probe skipped:', starterProbeError.message);
        }
      }

      let donorSlide = null;
      let cleanSlide = null;
      let runtimeMaster = null;
      let runtimeLayout = null;
      let runtimeLayouts = [];
      try {
        const insertionOptions = {
          formatting: PowerPoint.InsertSlideFormatting.keepSourceFormatting,
          sourceSlideIds: [design.donorSlideId],
        };
        const lastOriginalSlide = originalSlides[originalSlides.length - 1];
        if (lastOriginalSlide?.id) insertionOptions.targetSlideId = lastOriginalSlide.id;

        presentation.insertSlidesFromBase64(carrierBase64, insertionOptions);
        await context.sync();

        slides.load('items/id');
        await context.sync();
        const donorCandidates = slides.items.filter((slide) => !originalSlideIds.has(slide.id));
        if (donorCandidates.length !== 1) {
          throw new Error(`Template carrier insertion created ${donorCandidates.length} slides instead of one`);
        }
        [donorSlide] = donorCandidates;

        donorSlide.layout.load('id,name');
        donorSlide.slideMaster.load('id,name');
        donorSlide.slideMaster.layouts.load('items/id,items/name');
        await context.sync();

        runtimeMaster = {
          id: donorSlide.slideMaster.id,
          name: donorSlide.slideMaster.name,
        };
        runtimeLayout = {
          id: donorSlide.layout.id,
          name: donorSlide.layout.name,
        };
        runtimeLayouts = donorSlide.slideMaster.layouts.items.map((layout) => ({
          id: layout.id,
          name: layout.name,
        }));

        slides.add({
          slideMasterId: donorSlide.slideMaster.id,
          layoutId: donorSlide.layout.id,
        });
        await context.sync();

        slides.load('items/id');
        await context.sync();
        const cleanCandidates = slides.items.filter((slide) => (
          !originalSlideIds.has(slide.id) && slide.id !== donorSlide.id
        ));
        if (cleanCandidates.length !== 1) {
          throw new Error(`Template clean-slide creation produced ${cleanCandidates.length} slides instead of one`);
        }
        [cleanSlide] = cleanCandidates;

        donorSlide.delete();
        await context.sync();
      } catch (error) {
        try {
          slides.load('items/id');
          await context.sync();
          for (const slide of slides.items) {
            if (!originalSlideIds.has(slide.id)) slide.delete();
          }
          await context.sync();
        } catch (cleanupError) {
          console.error('[PowerPointBridge] Template carrier cleanup failed:', cleanupError.message);
        }
        throw error;
      }

      let replacedBlankStarter = false;
      if (blankStarter && starterSlide) {
        try {
          starterSlide.delete();
          await context.sync();
          replacedBlankStarter = true;
        } catch (starterDeleteError) {
          console.warn('[PowerPointBridge] Blank starter was preserved:', starterDeleteError.message);
        }
      }

      try {
        for (const key of Object.values(TEMPLATE_TAG_KEYS)) {
          if (existingTags.has(key)) tags.delete(key);
        }
        tags.add(TEMPLATE_TAG_KEYS.fingerprint, design.fingerprint);
        tags.add(TEMPLATE_TAG_KEYS.masterId, runtimeMaster.id);
        tags.add(TEMPLATE_TAG_KEYS.layoutId, runtimeLayout.id);
        tags.add(TEMPLATE_TAG_KEYS.workingSlideId, cleanSlide.id);
        if (requestedApplyScope) tags.add(TEMPLATE_TAG_KEYS.applyScope, requestedApplyScope);
        await context.sync();
      } catch (tagWriteError) {
        console.warn('[PowerPointBridge] Template idempotence tag write skipped:', tagWriteError.message);
      }

      try {
        presentation.setSelectedSlides([cleanSlide.id]);
        await context.sync();
      } catch (selectionError) {
        console.warn('[PowerPointBridge] Template working-slide selection skipped:', selectionError.message);
      }

      slides.load('items/id');
      await context.sync();
      return {
        success: true,
        idempotent: false,
        designAlreadyApplied: false,
        templateFingerprint: design.fingerprint,
        sourceSlidesRetained: 0,
        temporaryCarrierRemoved: true,
        createdSlides: 1,
        replacedBlankStarter,
        totalSlides: slides.items.length,
        workingSlideId: cleanSlide.id,
        master: runtimeMaster,
        workingLayout: runtimeLayout,
        layouts: runtimeLayouts,
        design,
        note: 'Template master, layouts, and theme applied. The working slide is clean and no template example slides remain.',
      };
    }, { label: 'apply_template', timeoutMs: 120000 });
  }
}
